import { FC, Fragment, memo, useMemo } from 'react';
import styled from '@emotion/styled';
import Spinner from '../primitive/Spinner';
import DesktopWindowsIcon from '@material-ui/icons/DesktopWindows';
import SmartphoneIcon from '@material-ui/icons/Smartphone';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { COLOR_BORDER, COLOR_SECONDARY_BACKGROUND, COLOR_SHADOW, COLOR_TEXT_MAIN } from '../../../theme/colors';
import { Project } from '../../../generated/graphql';
import { useNavigate } from 'react-router';

/**
 * 一覧の各エクスペリエンス-グレー背景エリア
 */
const _ExperimentOverview: FC<{ project: Project }> = ({ project }) => {
  const navigate = useNavigate();
  const projectStateMemo = useMemo(() => {
    {
      if (project.state === 'competition') {
        return '計測中';
      }
      if (project.state === 'initial') {
        return '準備中';
      }
      if (project.state === 'archived') {
        return '終了';
      }
      if (project.state === 'interim') {
        return 'インターバル';
      }
      return '-';
    }
  }, [project.state]);

  const projectScoreMemo = useMemo(() => {
    if (project.score === null) {
      return '--%';
    }
    if (project.score !== '0.0') {
      return `${Number(project?.score) * 100}%`;
    }
    return '--%';
  }, [project.score]);

  return (
    <DetailDisplayArea>
      {/* ColumnLabel */}
      <FlexColumnLabel>
        <FlexItem>デバイス</FlexItem>
        <FlexItem>ラウンド</FlexItem>
        <FlexItem>改善率</FlexItem>
        <FlexItem></FlexItem>
      </FlexColumnLabel>
      <FlexColumnContent>
        {/* デバイス */}
        <FlexItem>
          <DeviceIconWrapper>
            {project ? (
              project?.is_mobile_web ? (
                <Fragment>
                  <SmartphoneIcon />
                  <MarginLeft>Mobile</MarginLeft>
                </Fragment>
              ) : (
                <Fragment>
                  <DesktopWindowsIcon />
                  <MarginLeft>DeskTop</MarginLeft>
                </Fragment>
              )
            ) : (
              <Spinner />
            )}
          </DeviceIconWrapper>
        </FlexItem>

        {/* ラウンド */}
        <FlexItem>{projectStateMemo}</FlexItem>

        {/* 改善率 */}
        <FlexItem>{projectScoreMemo}</FlexItem>

        {/* 詳細へのリンク */}
        <FlexItem>
          <TextLink
            onClick={() => {
              navigate(`/engine/experiments_b/${project.id}`);
            }}
          >
            <span>詳細を見る</span>
            <NavigateNextIcon fontSize="small" />
          </TextLink>
        </FlexItem>
      </FlexColumnContent>
    </DetailDisplayArea>
  );
};

export const ExperimentOverview = memo(_ExperimentOverview);

const FlexColumnLabel = styled('div')(`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 0 16px;
`);

const FlexColumnContent = styled('div')(`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  font-weight: 700;
  padding: 12px 16px;
  background: ${COLOR_SECONDARY_BACKGROUND};
  box-shadow: 0px 1px 9px ${COLOR_SHADOW};
  border-radius: 8px;
  border: 1px solid ${COLOR_BORDER};
  height: 48px;
  width: 100%;
  margin: 10.5px 0 0 0;
`);

const TextLink = styled('div')(`
  width: 80px;
  display: flex;
  align-items: center;
  text-decoration: none;
  cursor: pointer;
  color: ${COLOR_TEXT_MAIN};
`);

const DetailDisplayArea = styled('div')(`
  margin-top: 16px;
`);

const DeviceIconWrapper = styled('span')(`
  width: 100px;
  display: flex;
  align-items: center;
`);

const FlexItem = styled('span')(`
  flex-basis: 80px;
`);

const MarginLeft = styled('span')(`
  margin-left: 12px;
`);
